import { Duration, DateTime } from 'luxon'
import de from 'numbro/languages/de-DE'
import numbro from 'numbro'
import sortOn from 'sort-on'
import { marked } from 'marked'
import { partial } from 'filesize'

numbro.registerLanguage(de, true)
numbro.setLanguage('de-DE')

export function useTemplateFilter () {
  const toMd = (value) => {
    return marked.parse(value, {
      mangle: false,
      headerIds: false
    })
  }

  const formatAccounId = (value) => {
    if (!value) return
    return numbro(value).format({ thousandSeparated: true })
  }

  const formatDuration = (value) => {
    if (!value) return
    const dur = Duration.fromObject({ minutes: value })
    return dur.toFormat('h:mm')
  }

  const formatLegacyDate = (value, format = true, add = false) => {
    const date = DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss')

    if (add) {
      date.plus({ days: add })
    }

    return date.toFormat('dd.MM.yyyy', { land: 'de' })
  }

  const formatDeDateTime = (value, format = 'dd.MM.yyyy HH:mm') => {
    const date = DateTime.fromFormat(value, 'dd.MM.yyyy HH:mm')

    return date.toFormat(format, { lang: 'de' })
  }

  const isToday = (date) => {
    return date.toISODate() === DateTime.now().toISODate()
  }

  const isYesterday = (date) => {
    return date.toISODate() === DateTime.now().minus({ days: 1 }).toISODate()
  }

  const formatDateOfReceipt = (value, format = 'dd.MM.yyyy HH:mm') => {
    const date = DateTime.fromFormat(value, format)
    if (isToday(date)) {
      return date.toFormat('HH:mm')
    }

    if (isYesterday(date)) {
      return 'gest.'
    }

    const diffInDays = DateTime.now().diff(date, 'days')

    if (diffInDays.days < 7) {
      return weekdayByNumberForLuxon(date.weekday) + '.'
    }

    return date.toFormat('dd.MM.')
  }

  const formatDeDateToISO = (value, format = true, add = false) => {
    const date = DateTime.fromFormat(value, 'dd.MM.yyyy HH:mm')
    return date.toISO()
  }

  const formatDeDate = (value, format = true, add = false) => {
    const date = DateTime.fromFormat(value, 'dd.MM.yyyy')

    if (add) {
      date.plus({ days: add })
    }

    return format ? date.toFormat('yyyy-MM-dd') : date.toMillis()
  }

  const formatDate = (value, formatString = 'dd.MM.yyyy') => {
    if (!value) return ''
    return DateTime.fromISO(value).toFormat(formatString, { lang: 'de' })
  }

  const formatFileSize = (value) => {
    value = value * 1000
    const size = partial({ base: 2, standard: 'jedec', separator: ',', round: 2 })

    return size(value)
  }

  const weekdayByNumberForLuxon = (value) => {
    switch (value) {
      case 1:
        return 'Mo'
      case 2:
        return 'Di'
      case 3:
        return 'Mi'
      case 4:
        return 'Do'
      case 5:
        return 'Fr'
      case 6:
        return 'Sa'
      case 7:
        return 'So'
    }
  }

  const weekdayByNumber = (value) => {
    switch (value) {
      case 0:
        return 'So'
      case 1:
        return 'Mo'
      case 2:
        return 'Di'
      case 3:
        return 'Mi'
      case 4:
        return 'Do'
      case 5:
        return 'Fr'
      case 6:
        return 'Sa'
      case 7:
        return 'Es gibt nur 7 Wochenentage'
    }
  }

  const formatWeekdays = (value) => {
    if (!value) return ''
    if (!Array.isArray(value)) {
      value = value.split(',')
    }

    value = sortOn(value)

    const weekdays = value.map(item => {
      return weekdayByNumber(item)
    })

    return weekdays.join(', ')
  }

  const formatDateTime = (value, format = 'dd.MM.yyyy HH:mm') => {
    if (!value) return ''
    return DateTime.fromISO(value).toFormat(format)
  }

  const formatCurrency = (value = 0) => {
    return numbro(value).formatCurrency({ mantissa: 2, thousandSeparated: true, spaceSeparated: true })
  }

  const formatFloat = (value, unit = '') => {
    return (numbro(value).format({ mantissa: 2, thousandSeparated: true }) + ' ' + unit).trim()
  }

  const formatInt = (value, unit = '') => {
    if (!value) return 0
    return (numbro(value).format({ thousandSeparated: true }) + ' ' + unit).trim()
  }

  function getImageUrl (name) {
    return new URL(`/${name}`, import.meta.env.VITE_APP_ASSETS).href
  }

  const formatBytes = (value) => {
    if (!value) return
    return numbro(value).format({
      output: 'byte',
      spaceSeparated: true,
      base: 'binary',
      mantissa: 1
    }).replace('KiB', 'KB')
  }

  const nl2br = (str, isXHTML = true) => {
    if (typeof str === 'undefined' || str === null) {
      return ''
    }
    const breakTag = (isXHTML || typeof isXHTML === 'undefined') ? '<br />' : '<br>'
    return (str + '').replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, '$1' + breakTag + '$2')
  }

  return { toMd, formatFileSize, formatDeDateToISO, formatAccounId, formatDeDateTime, formatDateOfReceipt, formatCurrency, formatLegacyDate, formatDuration, formatWeekdays, getImageUrl, formatFloat, formatBytes, formatInt, formatDate, formatDateTime, formatDeDate, nl2br }
}
